import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ArrowLeft, MessageSquare, Plus, Check } from "lucide-react";

export default function FollowersPage({ mode = "followers" }) {
  const { handle } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/users/${handle}/${mode}`);
      setRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load list.");
      setRows([]);
    }
  }, [handle, mode]);
  useEffect(() => { load(); }, [load]);

  const toggleFollow = async (u) => {
    try {
      if (u.is_followed_by_me) {
        await api.delete(`/users/${u.id}/follow`);
        toast.success(`Unfollowed ${u.display_name}`);
      } else {
        await api.post(`/users/${u.id}/follow`);
        toast.success(`Following ${u.display_name}`);
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update follow."); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-2xl mx-auto" data-testid={`${mode}-page-root`}>
      <Link to={`/u/${handle}`} className="inline-flex items-center gap-1.5 text-rose-500 hover:text-rose-400 text-sm mb-4">
        <ArrowLeft size={14} /> Back to @{handle}
      </Link>
      <h1 className="font-display text-3xl mb-2 capitalize">{mode}</h1>
      <div className="flex gap-2 mb-5 text-xs uppercase tracking-widest">
        <Link
          to={`/u/${handle}/followers`}
          data-testid="followers-tab"
          className={`px-3 py-1.5 rounded-full border ${mode === "followers" ? "bg-rose-600 border-rose-600 text-white" : "border-white/10 text-zinc-400 hover:text-white"}`}
        >Followers</Link>
        <Link
          to={`/u/${handle}/following`}
          data-testid="following-tab"
          className={`px-3 py-1.5 rounded-full border ${mode === "following" ? "bg-rose-600 border-rose-600 text-white" : "border-white/10 text-zinc-400 hover:text-white"}`}
        >Following</Link>
      </div>
      {rows === null ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="card-surface p-10 text-center text-sm text-zinc-500">
          {mode === "followers" ? "No followers yet." : "Not following anyone yet."}
        </div>
      ) : (
        <div className="space-y-2" data-testid={`${mode}-list`}>
          {rows.map((u) => (
            <div key={u.id} className="card-surface p-3 flex items-center gap-3" data-testid={`follow-row-${u.id}`}>
              <Link to={`/u/${u.id}`}>
                <Avatar className={`w-11 h-11 border border-white/10`}>
                  <AvatarImage src={u.avatar_url} />
                  <AvatarFallback className="bg-rose-600 text-white text-sm">
                    {u.display_name?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </Link>
              <Link to={`/u/${u.id}`} className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className={`text-sm font-medium truncate`}>
                    {u.display_name}
                  </span>
                </div>
                <div className="text-xs text-zinc-500 flex items-center gap-2">
                  {u.display_name}
                  {u.shared_desires_count > 0 && (
                    <span className="inline-flex items-center gap-0.5 rounded-full border border-rose-500/40 bg-rose-950/30 px-1.5 py-0.5 text-[10px] text-rose-300">
                      ✨ {u.shared_desires_count}
                    </span>
                  )}
                </div>
              </Link>
              <div className="flex gap-1.5 shrink-0">
                <Button
                  onClick={() => navigate(`/inbox?to=${u.id}`)}
                  variant="ghost"
                  className="rounded-full h-8 w-8 p-0 border border-white/15"
                  data-testid={`follow-row-message-${u.id}`}
                  title="Message"
                >
                  <MessageSquare size={13} />
                </Button>
                <Button
                  onClick={() => toggleFollow(u)}
                  className={`rounded-full h-8 px-3 text-xs ${
                    u.is_followed_by_me
                      ? "bg-transparent border border-rose-500/60 text-rose-400"
                      : "btn-crimson"
                  }`}
                  data-testid={`follow-row-toggle-${u.id}`}
                >
                  {u.is_followed_by_me ? <><Check size={11} className="mr-1" /> Following</> : <><Plus size={11} className="mr-1" /> Follow</>}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
